import { useState, useEffect } from 'react';
import { Calendar, Clock, Edit, Trash2 } from 'lucide-react';
import { client } from '@/api';

const AvailabilityDisplay = ({ astrologerId, onEditClick }) => {
    const [weeklySchedule, setWeeklySchedule] = useState(null);
    const [specificDates, setSpecificDates] = useState([]);
    const [loading, setLoading] = useState(true);

    const daysOfWeek = [
        { key: 'sunday', label: 'Sunday' },
        { key: 'monday', label: 'Monday' },
        { key: 'tuesday', label: 'Tuesday' },
        { key: 'wednesday', label: 'Wednesday' },
        { key: 'thursday', label: 'Thursday' },
        { key: 'friday', label: 'Friday' },
        { key: 'saturday', label: 'Saturday' }
    ];

    useEffect(() => {
        if (astrologerId) {
            fetchAvailability();
        }
    }, [astrologerId]);

    const fetchAvailability = async () => {
        try {
            const { data } = await client.get('/api/meeting/weekly-availability', {
                params: { astrologerId }
            });

            if (data.success) {
                setWeeklySchedule(data.data.weeklySchedule);
                setSpecificDates(data.data.specificDates || []);
            }
        } catch (error) {
            console.error('Error fetching availability:', error);
        } finally {
            setLoading(false);
        }
    };

    const removeSpecificDate = async (dateId) => {
        try {
            await client.delete(`/api/meeting/specific-date/${dateId}`);
            setSpecificDates(prev => prev.filter(date => date._id !== dateId));
        } catch (error) {
            console.error('Error removing specific date:', error);
        }
    };

    const formatDateRange = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-500 border-t-transparent"></div>
            </div>
        );
    }

    if (!weeklySchedule) {
        return (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200 shadow-sm">
                <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Set Your Availability</h3>
                <p className="text-gray-500 mb-6 text-sm">
                    By setting your availability, you can start your consultation journey
                </p>
                <button
                    onClick={onEditClick}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-md transition-colors text-sm font-medium"
                >
                    Set Availability
                </button>
            </div>
        );
    }

    const enabledDays = daysOfWeek.filter(day => weeklySchedule[day.key]?.enabled);

    return (
        <div className="space-y-6">
            {/* Working Hours */}
            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                        <Clock className="h-5 w-5 text-orange-500" />
                        <h3 className="font-semibold text-gray-900">Working Hours</h3>
                    </div>
                    <button
                        onClick={onEditClick}
                        className="p-2 text-gray-500 hover:text-orange-500 rounded-full transition-colors"
                        title="Edit"
                    >
                        <Edit className="h-4 w-4" />
                    </button>
                </div>

                {enabledDays.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                        {enabledDays.map((day) => {
                            const schedule = weeklySchedule[day.key];
                            return (
                                <div key={day.key} className="py-2">
                                    <span className="text-sm font-medium text-gray-800">{day.label}</span>
                                    {schedule.slots?.length > 0 ? (
                                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                                            {schedule.slots.map((slot, idx) => (
                                                <span
                                                    key={idx}
                                                    className="text-sm text-gray-600 bg-gray-50 px-2 py-0.5 rounded-md border border-gray-200"
                                                >
                                                    {slot.startTime} - {slot.endTime}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-gray-400">No slots</p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="text-sm text-gray-500">No working days set</p>
                )}
            </div>

            {/* Specific Dates */}
            {specificDates.length > 0 && (
                <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center space-x-2 mb-4">
                        <Calendar className="h-5 w-5 text-orange-500" />
                        <h3 className="font-semibold text-gray-900">Special Dates</h3>
                    </div>

                    <div className="space-y-3">
                        {specificDates.map((specificDate) => (
                            <div
                                key={specificDate._id}
                                className="flex items-center justify-between bg-gray-50 p-3 rounded-md border border-gray-200"
                            >
                                <div>
                                    <p className="font-medium text-gray-900">{formatDateRange(specificDate.date)}</p>
                                    <div className="flex items-center space-x-2 mt-1">
                                        <span
                                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${specificDate.type === 'available'
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-red-100 text-red-800'
                                                }`}
                                        >
                                            {specificDate.type === 'available' ? 'Available' : 'Unavailable'}
                                        </span>
                                        {specificDate.type === 'available' && (
                                            <span className="text-sm text-gray-600">
                                                {specificDate.startTime} - {specificDate.endTime}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={() => removeSpecificDate(specificDate._id)}
                                    className="p-2 text-red-500 hover:text-red-700 rounded-full transition-colors"
                                    title="Delete"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AvailabilityDisplay;